import { useState, useRef } from "react";
import SortableTicketList from "../sortableTicketList/SortableTicketList";
import FormField from "../../formField/FormField";
import "./ticketForm.scss";
import PropTypes from "prop-types";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import OutlinedPinkButton from "../../buttons/outlinedPinkButton/OutlinedPinkButton";
import GreenRedOutlinedButtons from "../../buttons/greenRedOutlinedButtons/GreenRedOutlinedButtons";
import useTicketValidationSchema from "../../validationsSchemas/useTicketValidationSchema";
import { Form } from "react-bootstrap";
import dayjs from "dayjs";
import Modal from "../../modal/Modal";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const createEmptyOptionValue = () => ({
  id: String(Date.now() + Math.floor(Math.random() * 1000)),
  label: "",
  priceDelta: "0",
  stock: "",
  amountSold: 0,
});

const createEmptyOptionGroup = () => ({
  id: String(Date.now() + Math.floor(Math.random() * 1000)),
  name: "",
  required: true,
  selectionType: "single",
  maxSelect: "",
  values: [createEmptyOptionValue()],
});

function TicketForm({ tickets, handleCreateUpdate, handleReorder, handleDelete, error }) {
  const { t } = useTranslation("ticketForm");
  const ticketFormRef = useRef(null);
  const [newTicket, setNewTicket] = useState({});
  const [errors, setErrors] = useState({});
  const [showDescription, setShowDescription] = useState(false);
  const [showAvailabilityDates, setShowAvailabilityDates] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const imageInputRef = useRef(null);
  const ticketToDelete = useRef(null);
  const ticketToEdit = useRef(null);
  const alert = useAlert();

  const validationSchema = useTicketValidationSchema();

  async function compressImage(file, ticketId) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = 250;
          canvas.height = 250;

          const ctx = canvas.getContext("2d");
          const scale = Math.max(250 / img.width, 250 / img.height);
          const x = 250 / 2 - (img.width / 2) * scale;
          const y = 250 / 2 - (img.height / 2) * scale;

          ctx.fillStyle = "#fff";
          ctx.fillRect(0, 0, 250, 250);
          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

          canvas.toBlob(
            async (blob) => {
              try {
                const storage = getStorage();
                const storageRef = ref(storage, `tickets/${ticketId}/icon.jpg`);
                await uploadBytes(storageRef, blob);
                const url = await getDownloadURL(storageRef);
                resolve(url);
              } catch (uploadError) {
                reject(new Error("Failed to upload image to storage"));
              }
            },
            "image/jpeg",
            0.8,
          );
        };
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (file) {
      const maxFileSize = 5 * 1024 * 1024;
      if (file.size > maxFileSize) {
        alert.show(t("fileSizeExceedsLimit"));
        if (imageInputRef.current) imageInputRef.current.value = "";
        return;
      }

      try {
        let ticketId = newTicket.id;
        if (ticketToEdit.current === null && !ticketId) {
          ticketId = String(Date.now());
          updateState("id", ticketId);
        }

        const compressedImage = await compressImage(file, ticketId);
        updateState("image", compressedImage);
      } catch (err) {
        alert.show(t("imageProcessingError"));
        if (imageInputRef.current) imageInputRef.current.value = "";
        console.error(err);
      }
    }
  }

  function normalizeOptions(options = []) {
    if (!Array.isArray(options)) return [];

    return options
      .map((group) => {
        const normalizedValues = (group.values || [])
          .map((value) => ({
            id: value.id || String(Date.now() + Math.floor(Math.random() * 1000)),
            label: value.label,
            priceDelta:
              typeof value.priceDelta === "string"
                ? parseFloat(value.priceDelta.replace(",", ".")) || 0
                : parseFloat(value.priceDelta || 0),
            stock:
              value.stock === "" || value.stock === null || value.stock === undefined
                ? null
                : parseInt(value.stock),
            amountSold: parseInt(value.amountSold || 0),
          }))
          .filter((value) => value.label && !Number.isNaN(value.priceDelta));

        return {
          id: group.id || String(Date.now() + Math.floor(Math.random() * 1000)),
          name: group.name,
          required: !!group.required,
          selectionType: group.selectionType === "multi" ? "multi" : "single",
          maxSelect:
            group.maxSelect === "" || group.maxSelect === null || group.maxSelect === undefined
              ? null
              : parseInt(group.maxSelect),
          values: normalizedValues,
        };
      })
      .filter((group) => group.name && group.values.length > 0);
  }

  async function validationForm() {
    try {
      setErrors({});

      const priceUpdated =
        typeof newTicket.price === "string" ? newTicket.price.replace(",", ".") : newTicket.price;

      const optionsUpdated = normalizeOptions(newTicket.options || []);
      const validateTicket = await validationSchema.validate(
        {
          ...newTicket,
          price: priceUpdated,
          options: optionsUpdated,
        },
        { abortEarly: false },
      );

      let ticketUpdated = {
        ...validateTicket,
        maxUnitPerSale: parseInt(newTicket.maxUnitPerSale),
        limitPerEmail: newTicket.limitPerEmail === true,
        price: parseFloat(priceUpdated),
        availableFrom: newTicket.availableFrom
          ? dayjs(newTicket.availableFrom).startOf("day").valueOf()
          : null,
        availableTo: newTicket.availableTo
          ? dayjs(newTicket.availableTo).startOf("day").valueOf()
          : null,
        options: showOptions ? optionsUpdated : [],
      };

      if (ticketToEdit.current !== null) {
        ticketUpdated.numAvailable =
          parseInt(newTicket.numAvailable) + parseInt(newTicket.amountSold);
      } else {
        ticketUpdated.numAvailable = parseInt(newTicket.numAvailable);
        ticketUpdated.amountSold = 0;
      }

      const isEditingTicket = ticketToEdit.current !== null;
      if (isEditingTicket) {
        handleCreateUpdate({ ...ticketUpdated, isNew: false });
      } else {
        handleCreateUpdate({
          ...ticketUpdated,
          isNew: true,
          id: ticketUpdated.id || String(Date.now()),
        });
      }

      ticketToEdit.current = null;
      setNewTicket({});
      setShowDescription(false);
      setShowAvailabilityDates(false);
      setShowImage(false);
      setShowOptions(false);
      setIsTicketModalOpen(false);
    } catch (err) {
      if (Array.isArray(err?.inner)) {
        const newErrors = err.inner.reduce((acc, curr) => {
          acc[curr.path] = curr.message;
          return acc;
        }, {});
        setErrors(newErrors);
      }
      alert.show(t("someFieldsAreIncorrect"));
    }
  }

  function updateState(name, value) {
    setNewTicket((prevNewTicket) => ({
      ...prevNewTicket,
      [name]: value,
    }));
  }

  function updateOptionGroup(groupIndex, patch) {
    const options = [...(newTicket.options || [])];
    options[groupIndex] = { ...options[groupIndex], ...patch };
    updateState("options", options);
  }

  function addOptionGroup() {
    updateState("options", [...(newTicket.options || []), createEmptyOptionGroup()]);
  }

  function removeOptionGroup(groupIndex) {
    const options = [...(newTicket.options || [])];
    options.splice(groupIndex, 1);
    updateState("options", options);
  }

  function addOptionValue(groupIndex) {
    const options = [...(newTicket.options || [])];
    options[groupIndex] = {
      ...options[groupIndex],
      values: [...(options[groupIndex].values || []), createEmptyOptionValue()],
    };
    updateState("options", options);
  }

  function removeOptionValue(groupIndex, valueIndex) {
    const options = [...(newTicket.options || [])];
    const values = [...(options[groupIndex].values || [])];
    values.splice(valueIndex, 1);
    options[groupIndex] = { ...options[groupIndex], values };
    updateState("options", options);
  }

  function updateOptionValue(groupIndex, valueIndex, patch) {
    const options = [...(newTicket.options || [])];
    const values = [...(options[groupIndex].values || [])];
    values[valueIndex] = { ...values[valueIndex], ...patch };
    options[groupIndex] = { ...options[groupIndex], values };
    updateState("options", options);
  }

  function handleUpdate(index) {
    ticketToEdit.current = index;
    if (tickets[index].description) {
      setShowDescription(true);
    }
    if (tickets[index].availableFrom || tickets[index].availableTo) {
      setShowAvailabilityDates(true);
    }
    if (tickets[index].image) {
      setShowImage(true);
    }

    const hasOptions = Array.isArray(tickets[index].options) && tickets[index].options.length > 0;
    setShowOptions(hasOptions);

    setNewTicket({
      ...tickets[index],
      options:
        tickets[index].options?.map((group) => ({
          ...group,
          maxSelect: group.maxSelect ?? "",
          values: (group.values || []).map((value) => ({
            ...value,
            stock: value.stock ?? "",
          })),
        })) || [],
      numAvailable: tickets[index].numAvailable - tickets[index].amountSold,
      availableFrom: tickets[index].availableFrom ? new Date(tickets[index].availableFrom) : null,
      availableTo: tickets[index].availableTo ? new Date(tickets[index].availableTo) : null,
    });

    setIsTicketModalOpen(true);
  }

  function handleDeleteVerification(index) {
    ticketToDelete.current = index;
    setShowDeleteModal(true);
  }

  function priceVerification() {
    if (
      tickets[ticketToEdit.current].isNew ||
      !newTicket.price ||
      tickets[ticketToEdit.current].price === newTicket.price
    ) {
      validationForm();
    } else {
      setShowPriceModal(true);
    }
  }

  function onPriceModalClose() {
    setShowPriceModal(false);
  }

  function resetCurrentTicket() {
    setNewTicket({});
    setShowDescription(false);
    setShowAvailabilityDates(false);
    setShowImage(false);
    setShowOptions(false);
    ticketToEdit.current = null;
    setErrors({});
    setIsTicketModalOpen(false);
  }

  function openNewTicketModal() {
    resetCurrentTicket();
    setIsTicketModalOpen(true);
  }

  function onPriceModalValidation() {
    setShowPriceModal(false);
    validationForm();
  }

  function onDeleteModalClose() {
    setShowDeleteModal(false);
    ticketToDelete.current = null;
  }

  function onDeleteModalValidation() {
    setShowDeleteModal(false);
    handleDelete(ticketToDelete.current);
    ticketToDelete.current = null;
    resetCurrentTicket();
  }

  return (
    <>
      <Modal
        isOpen={showPriceModal}
        onClose={onPriceModalClose}
        onValidate={onPriceModalValidation}
        title={t("alert")}
        message={t("ticketPriceChangeWarning")}
      />
      <Modal
        isOpen={showDeleteModal}
        onClose={onDeleteModalClose}
        onValidate={onDeleteModalValidation}
        title={t("alert")}
        message={
          tickets[ticketToDelete.current]?.isNew
            ? t("deleteNewTicketWarning")
            : t("deleteTicketWarning")
        }
      />

      <section className="ticketSectionContainer">
        {isTicketModalOpen && (
          <div className="ticketFormModalOverlay" role="presentation" onMouseDown={resetCurrentTicket}>
            <div
              ref={ticketFormRef}
              className="ticketFormModal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ticket-form-modal-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="ticketFormModalHeader">
                <div>
                  <p className="ticketFormModalEyebrow">{t("addTicketsToSale")}</p>
                  <h2 id="ticket-form-modal-title">
                    {ticketToEdit.current !== null ? t("editTicketTitle") : t("createTicketTitle")}
                  </h2>
                  <span>{t("ticketModalSubtitle")}</span>
                </div>
                <button
                  type="button"
                  className="ticketFormModalClose"
                  onClick={resetCurrentTicket}
                  aria-label={t("dismiss")}
                >
                  ×
                </button>
              </div>

              <div className="ticketFormModalBody">
                <div className="ticketFormContainer">
          <div className="ticketFormFields">
            <FormField
              label={t("ticketName")}
              value={newTicket.name || ""}
              error={errors.name}
              onChange={(value) => updateState("name", value)}
              type="text"
              secondaryTheme
            />
            <FormField
              label={t("price")}
              value={newTicket.price ?? ""}
              error={errors.price}
              onChange={(value) => {
                if (/^$|^[0-9]+([.,][0-9]{0,2})?$/.test(value)) updateState("price", value);
              }}
              secondaryTheme
            />
            <FormField
              label={t("numberAvailable")}
              value={newTicket.numAvailable ?? ""}
              error={errors.numAvailable}
              onChange={(value) => {
                if (/^[0-9]*$/.test(value)) updateState("numAvailable", value);
              }}
              secondaryTheme
            />
            <FormField
              label={t("maxUnitPerSale")}
              value={newTicket.maxUnitPerSale || ""}
              error={errors.maxUnitPerSale}
              onChange={(value) => {
                if (/^$|^[1-9][0-9]*$/.test(value)) updateState("maxUnitPerSale", value);
              }}
              secondaryTheme
            />
          </div>
          <div className="ticketDescription">
            <div className="ticketShowDescription ticketEmailLimit">
              <div>
                <label htmlFor="ticket-limit-per-email">{t("limitPerEmail")}</label>
                <p className="ticketLimitHelp">{t("limitPerEmailHelp")}</p>
              </div>
              <Form.Check
                id="ticket-limit-per-email"
                type="switch"
                aria-label={t("limitPerEmail")}
                checked={newTicket.limitPerEmail === true}
                onChange={(e) => updateState("limitPerEmail", e.target.checked)}
              />
            </div>
          </div>
          <div className="ticketDescription">
            <div className="ticketShowDescription">
              <p>{t("addDesc")}</p>
              <Form.Check
                type="switch"
                checked={showDescription}
                onChange={(e) => {
                  setShowDescription(e.target.checked);
                  updateState("description", "");
                }}
              />
            </div>
            {showDescription && (
              <FormField
                label={t("desc")}
                value={newTicket.description || ""}
                error={errors.description}
                onChange={(value) => updateState("description", value)}
                type="longText"
                secondaryTheme
              />
            )}
          </div>
          <div className="ticketSecretField">
            <p>{t("secretTicketQuestion")}</p>
            <Form.Check
              type="switch"
              checked={newTicket.secret}
              onChange={(e) => updateState("secret", e.target.checked)}
            />
          </div>

          <div className="ticketDescription">
            <div className="ticketShowDescription">
              <p>{t("hasOptions")}</p>
              <Form.Check
                type="switch"
                checked={showOptions}
                onChange={(e) => {
                  setShowOptions(e.target.checked);
                  if (e.target.checked && !Array.isArray(newTicket.options)) {
                    updateState("options", [createEmptyOptionGroup()]);
                  }
                  if (!e.target.checked) {
                    updateState("options", []);
                  }
                }}
              />
            </div>
            {showOptions && (
              <div className="ticketOptionsContainer">
                <div className="ticketOptionsHeader">
                  <h3>{t("optionsTitle")}</h3>
                  <p>{t("optionsDescription")}</p>
                </div>
                {(newTicket.options || []).map((group, groupIndex) => (
                  <div className="ticketOptionGroup" key={group.id || groupIndex}>
                    <div className="ticketOptionGroupHeader">
                      <p>{t("optionGroupIndex", { index: groupIndex + 1 })}</p>
                      <button
                        type="button"
                        className="ticketOptionDeleteButton"
                        onClick={() => removeOptionGroup(groupIndex)}
                      >
                        {t("removeGroup")}
                      </button>
                    </div>
                    <div className="ticketOptionGroupTop">
                      <FormField
                        label={t("optionGroupName")}
                        value={group.name || ""}
                        onChange={(value) => updateOptionGroup(groupIndex, { name: value })}
                        type="text"
                        secondaryTheme
                      />
                      <FormField
                        label={t("selectionType")}
                        value={group.selectionType || "single"}
                        onChange={(value) =>
                          updateOptionGroup(groupIndex, {
                            selectionType: value,
                            maxSelect: value === "single" ? "" : group.maxSelect,
                          })
                        }
                        type="dropdown"
                        dropDownValues={[
                          {
                            label: t("singleChoice"),
                            value: "single",
                          },
                          {
                            label: t("multipleChoice"),
                            value: "multi",
                          },
                        ]}
                        secondaryTheme
                      />
                      <FormField
                        label={t("maxSelections")}
                        value={group.maxSelect ?? ""}
                        onChange={(value) => {
                          if (/^$|^[1-9][0-9]*$/.test(value)) {
                            updateOptionGroup(groupIndex, { maxSelect: value });
                          }
                        }}
                        type="text"
                        placeholder={group.selectionType === "single" ? t("notUsedForSingle") : ""}
                        secondaryTheme
                      />
                    </div>

                    <div className="ticketShowDescription">
                      <p>{t("requiredSelection")}</p>
                      <Form.Check
                        type="switch"
                        checked={!!group.required}
                        onChange={(e) => updateOptionGroup(groupIndex, { required: e.target.checked })}
                      />
                    </div>

                    <div className="ticketOptionValues">
                      {(group.values || []).map((value, valueIndex) => (
                        <div className="ticketOptionValue" key={value.id || valueIndex}>
                          <FormField
                            label={t("optionLabel")}
                            value={value.label || ""}
                            onChange={(label) => updateOptionValue(groupIndex, valueIndex, { label })}
                            type="text"
                            secondaryTheme
                          />
                          <FormField
                            label={t("optionPriceDelta")}
                            value={value.priceDelta ?? "0"}
                            onChange={(priceDelta) => {
                              if (/^$|^[0-9]+([.,][0-9]{0,2})?$/.test(priceDelta)) {
                                updateOptionValue(groupIndex, valueIndex, { priceDelta });
                              }
                            }}
                            type="text"
                            secondaryTheme
                          />
                          <FormField
                            label={t("optionStock")}
                            value={value.stock ?? ""}
                            onChange={(stock) => {
                              if (/^$|^[0-9]+$/.test(stock)) {
                                updateOptionValue(groupIndex, valueIndex, { stock });
                              }
                            }}
                            type="text"
                            secondaryTheme
                          />
                          <button
                            type="button"
                            className="ticketOptionDeleteButton small"
                            onClick={() => removeOptionValue(groupIndex, valueIndex)}
                          >
                            {t("removeValue")}
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="ticketOptionActions">
                      <OutlinedPinkButton onPressFunction={() => addOptionValue(groupIndex)}>
                        {t("addOptionValue")}
                      </OutlinedPinkButton>
                    </div>
                  </div>
                ))}

                <div className="ticketOptionActions">
                  <OutlinedPinkButton onPressFunction={addOptionGroup}>
                    {t("addOptionGroup")}
                  </OutlinedPinkButton>
                </div>
                {errors.options && <p className="errorMessage">{errors.options}</p>}
              </div>
            )}
          </div>

          <div className="ticketDescription">
            <div className="ticketShowDescription">
              <p>{t("addAvailabilityDates")}</p>
              <Form.Check
                type="switch"
                checked={showAvailabilityDates}
                onChange={(e) => {
                  setShowAvailabilityDates(e.target.checked);
                  if (!e.target.checked) {
                    updateState("availableFrom", null);
                    updateState("availableTo", null);
                  }
                }}
              />
            </div>

            {showAvailabilityDates && (
              <div className="ticketFormFields">
                <FormField
                  label={t("availableFrom")}
                  value={newTicket.availableFrom || ""}
                  error={errors.availableFrom}
                  onChange={(value) => updateState("availableFrom", value)}
                  type="date"
                  secondaryTheme
                />
                <FormField
                  label={t("availableTo")}
                  value={newTicket.availableTo || ""}
                  error={errors.availableTo}
                  onChange={(value) => updateState("availableTo", value)}
                  type="date"
                  secondaryTheme
                />
              </div>
            )}
          </div>

          <div className="ticketDescription">
            <div className="ticketShowDescription">
              <p>{t("addImage")}</p>
              <Form.Check
                type="switch"
                checked={showImage}
                onChange={(e) => {
                  setShowImage(e.target.checked);
                  if (!e.target.checked) {
                    updateState("image", null);
                    if (imageInputRef.current) imageInputRef.current.value = "";
                  }
                }}
              />
            </div>
            {showImage && (
              <div className="ticketImageSection">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                />
                <button
                  className="ticketImageUploadButton"
                  onClick={() => imageInputRef.current?.click()}
                  type="button"
                >
                  {newTicket.image ? t("changeImage") : t("uploadImage")}
                </button>
                {newTicket.image && (
                  <div className="ticketImagePreview">
                    <img src={newTicket.image} alt="Ticket preview" />
                    <button
                      className="ticketImageRemoveButton"
                      onClick={() => {
                        updateState("image", null);
                        if (imageInputRef.current) {
                          imageInputRef.current.value = "";
                        }
                      }}
                      type="button"
                    >
                      {t("remove")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {ticketToEdit.current !== null ? (
            <GreenRedOutlinedButtons
              greenName={t("edit")}
              redName={t("dismiss")}
              greenOnPress={priceVerification}
              redOnPress={resetCurrentTicket}
            />
          ) : (
            <OutlinedPinkButton onPressFunction={validationForm}>
              {t("addNewTicket")}
            </OutlinedPinkButton>
          )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={`yourTicketContainer ${error ? "errorInput" : ""}`}>
          <div className="ticketListHeader">
            <div>
              <p className="sectionTitle">{t("yourListOfTickets")}</p>
              <span>{t("ticketListSubtitle", { count: tickets?.length || 0 })}</span>
            </div>
            <button type="button" className="ticketAddButton" onClick={openNewTicketModal}>
              + {t("addNewTicket")}
            </button>
          </div>
          <div className={`ticketsContainer ${tickets?.length > 0 ? "" : "noTickets"}`}>
            {tickets?.length > 0 ? (
              <SortableTicketList
                tickets={tickets}
                onReorder={handleReorder}
                handleEdit={handleUpdate}
                handleDelete={handleDeleteVerification}
              />
            ) : (
              <p>{t("noTicketsYet")}</p>
            )}
          </div>
        </div>
      </section>
      {error && <p className="errorMessage">{error}</p>}
    </>
  );
}

TicketForm.propTypes = {
  handleCreateUpdate: PropTypes.func.isRequired,
  handleReorder: PropTypes.func.isRequired,
  tickets: PropTypes.array.isRequired,
  handleDelete: PropTypes.func.isRequired,
  error: PropTypes.string,
};

export default TicketForm;
